<template>
  <div class="modal" :class="{ 'is-active': show }">
    <div class="modal-background" @click="$emit('close')"></div>
    <div class="modal-card cl7-changes-modal">
      <header class="modal-card-head">
        <p class="modal-card-title">
          <i class="material-icons">campaign</i>
          Hola, queremos contarte algo importante…
        </p>
        <button
          class="delete"
          aria-label="close"
          @click="$emit('close')"
        ></button>
      </header>

      <section class="modal-card-body">
        <!-- 1. Nivel de prioridad adicional -->
        <div class="cl7-block">
          <h3 class="cl7-block-title">Nivel de prioridad adicional</h3>
          <p>
            Estamos en constante revisión de nuestras encuestas y modelos de
            interpretación para que los resultados sean cada vez más claros, útiles y
            accionables.
          </p>
          <p>
            Inicialmente, el nivel <strong>«Cuidado»</strong> agrupaba un rango amplio
            de resultados. Aunque técnicamente era adecuado, la experiencia de uso y la
            escucha activa a nuestros clientes nos mostró que era necesario contar con
            una mayor capacidad de discriminación para facilitar la comprensión y
            priorización. Por eso, dividimos este nivel en dos categorías:
          </p>
          <ul class="cl7-split-list">
            <li>
              <span class="cl7-split-range">50% – 69.9%:</span>
              <span class="cl7-split-label">Estamos cerca</span>
            </li>
            <li>
              <span class="cl7-split-range">30% – 49.9%:</span>
              <span class="cl7-split-label">Cuidado</span>
            </li>
          </ul>
          <p>
            Este ajuste permite diferenciar mejor los resultados, priorizar con mayor
            criterio las intervenciones y visualizar con más claridad el impacto de la
            gestión y la evolución de los resultados en el tiempo.
          </p>
          <div class="cl7-priority-grid">
            <div
              v-for="lvl in priorityLevels"
              :key="lvl.level"
              class="cl7-priority-item"
              :class="{ 'is-new': lvl.isNew }"
            >
              <span
                class="cl7-priority-chip"
                :style="{ background: lvl.color }"
              ></span>
              <div class="cl7-priority-text">
                <strong>
                  {{ lvl.title }}
                  <span v-if="lvl.isNew" class="cl7-new-badge">Nuevo</span>
                </strong>
                <span class="cl7-priority-desc">{{ lvl.desc }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. Cálculo de indicadores -->
        <div class="cl7-block">
          <h3 class="cl7-block-title">Cálculo de indicadores</h3>
          <p>
            También ajustamos la forma de consolidar los indicadores generales del
            diagnóstico. Anteriormente, estos indicadores contaban con una fórmula de
            estandarización independiente, lo que en algunos casos podía generar
            diferencias entre la prioridad del indicador general y la lectura de las
            variables que lo componen.
          </p>
          <p>
            Con el ajuste realizado, el indicador general se construye a partir del
            promedio de las distribuciones de sus variables asociadas, permitiendo una
            lectura más coherente, intuitiva y alineada con el comportamiento real de
            los resultados.
          </p>
          <p>
            Este cambio aplica únicamente para los indicadores generales y de cuadrante.
            No modifica las respuestas de las personas ni las variables evaluadas; lo
            que cambia es la forma de consolidar e interpretar la prioridad global del
            resultado.
          </p>
          <p>
            Como resultado de este ajuste metodológico, algunos indicadores podrían
            presentar variaciones en su nivel de prioridad. Consideramos que esta
            actualización fortalece la consistencia de la lectura y facilita la toma de
            decisiones a partir de los datos. En caso de requerir mayor contexto o
            revisar el impacto específico en sus resultados, con gusto podremos
            profundizarlo en un espacio virtual.
          </p>
        </div>

        <!-- 3. Comparación de resultados -->
        <div class="cl7-block">
          <h3 class="cl7-block-title">
            Comparación de resultados — Toda la organización
          </h3>
          <p class="cl7-help">
            Porcentaje de personas en niveles alto y muy alto, con el cálculo anterior
            y el nuevo.
          </p>
          <table class="table is-fullwidth cl7-compare-table">
            <thead>
              <tr>
                <th>Indicador</th>
                <th class="has-text-centered">Cálculo anterior</th>
                <th class="has-text-centered">Cálculo nuevo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in comparison" :key="row.label">
                <td>{{ row.label }}</td>
                <td class="has-text-centered">
                  <span class="cl7-val" :style="valStyle(row.pOld)">
                    {{ fmt(row.pOld) }}
                  </span>
                </td>
                <td class="has-text-centered">
                  <span class="cl7-val is-bold" :style="valStyle(row.pNew)">
                    {{ fmt(row.pNew) }}
                  </span>
                </td>
                
              </tr>
              <tr v-if="!comparison.length">
                <td colspan="4" class="has-text-centered has-text-grey">
                  No hay datos suficientes para comparar.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <footer class="modal-card-foot">
        <button class="button is-dark" @click="$emit('close')">Entendido</button>
      </footer>
    </div>
  </div>
</template>

<script>
import { CLIMA_COLORS } from "./climaColors";

export default {
  name: "ModalCambiosCalculo",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // Array de filas { label, pOld, pNew } (pOld/pNew pueden ser null).
    comparison: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      priorityLevels: [
        {
          level: 1,
          color: CLIMA_COLORS[1],
          title: "Sigamos así",
          desc: "Si el 84% o más de las personas está en los niveles de alto y muy alto.",
        },
        {
          level: 2,
          color: CLIMA_COLORS[2],
          title: "Vamos bien",
          desc: "Entre el 70% y el 83.9% de personas en alto o muy alto.",
        },
        {
          level: 3,
          color: CLIMA_COLORS[3],
          title: "Estamos cerca",
          desc: "Entre el 50% y el 69.9% de personas en alto o muy alto.",
          isNew: true,
        },
        {
          level: 4,
          color: CLIMA_COLORS[4],
          title: "Cuidado",
          desc: "Entre el 30% y el 49.9% de personas en alto o muy alto.",
        },
        {
          level: 5,
          color: CLIMA_COLORS[5],
          title: "Necesita atención pronto",
          desc: "Entre el 16% y el 29.9% de personas en alto o muy alto.",
        },
        {
          level: 6,
          color: CLIMA_COLORS[6],
          title: "Actuemos ya",
          desc: "Menos del 16% de las personas en alto o muy alto.",
        },
      ],
    };
  },
  methods: {
    fmt(value) {
      if (value === null || value === undefined || !Number.isFinite(value))
        return "-";
      return `${value.toFixed(1)}%`;
    },
    // Color de la banda según el valor, igual que getInfoRange del tablero.
    valStyle(value) {
      if (value === null || value === undefined || !Number.isFinite(value))
        return {};
      const ranges = [
        { max: 15, color: CLIMA_COLORS[6] },
        { max: 30, color: CLIMA_COLORS[5] },
        { max: 50, color: CLIMA_COLORS[4] },
        { max: 70, color: CLIMA_COLORS[3] },
        { max: 84, color: CLIMA_COLORS[2] },
      ];
      const band = ranges.find((r) => value < r.max);
      return { background: band ? band.color : CLIMA_COLORS[1] };
    },
    diff(row) {
      if (
        row.pOld === null ||
        row.pNew === null ||
        !Number.isFinite(row.pOld) ||
        !Number.isFinite(row.pNew)
      )
        return null;
      return row.pNew - row.pOld;
    },
    diffLabel(row) {
      const d = this.diff(row);
      if (d === null) return "-";
      const arrow = d > 0 ? "▲" : d < 0 ? "▼" : "—";
      return `${arrow} ${Math.abs(d).toFixed(1)} pts`;
    },
    diffClass(row) {
      const d = this.diff(row);
      if (d === null || d === 0) return "cl7-diff-neutral";
      return d > 0 ? "cl7-diff-up" : "cl7-diff-down";
    },
  },
};
</script>

<style lang="scss" scoped>
.cl7-changes-modal {
  width: 720px;
  max-width: 95vw;
}

.modal-card-head {
  background: #510087;
}

.modal-card-title {
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  // Bulma pone flex-shrink: 0 en el título; lo permitimos encoger y envolver
  // para que no se corte en pantallas angostas.
  flex-shrink: 1;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
  line-height: 1.25;

  .material-icons {
    font-size: 22px;
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .modal-card-title {
    font-size: 1.15rem;
  }
}

.cl7-block {
  margin-bottom: 1.75rem;

  &:last-child {
    margin-bottom: 0;
  }

  p {
    margin-bottom: 0.5rem;
    color: #363636;
    line-height: 1.45;
  }
}

.cl7-block-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #510087;
  margin-bottom: 0.6rem;
}

.cl7-split-list {
  list-style: none;
  margin: 1.25rem 0;
  display: grid;
  grid-template-columns: max-content max-content;
  justify-content: center;
  column-gap: 0.4rem;
  row-gap: 0.3rem;
  color: #363636;
  line-height: 1.45;

  li {
    display: contents;
  }

  .cl7-split-range {
    text-align: right;
    font-weight: 700;
  }

  .cl7-split-label {
    text-align: left;
  }
}

.cl7-help {
  font-size: 0.85rem;
  color: #7a7a7a;
}

.cl7-priority-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem 1.25rem;
  margin-top: 0.6rem;
}

.cl7-priority-item {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;

  &.is-new {
    background: rgba(81, 0, 135, 0.06);
    box-shadow: inset 0 0 0 1.5px rgba(81, 0, 135, 0.35);
  }
}

.cl7-new-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 0 7px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #fff;
  background: #510087;
  border-radius: 10px;
  vertical-align: middle;
}

.cl7-priority-chip {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex: 0 0 auto;
  margin-top: 2px;
}

.cl7-priority-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  .cl7-priority-desc {
    font-size: 0.75rem;
    color: #5a5a5a;
    margin-top: 1px;
  }
}

.cl7-compare-table {
  margin-top: 0.5rem;

  th {
    color: #510087;
    border-bottom: 2px solid #ededed;
  }

  td {
    vertical-align: middle;
  }
}

.cl7-val {
  display: inline-block;
  min-width: 64px;
  padding: 2px 10px;
  border-radius: 12px;
  color: rgba(65, 64, 64, 0.91);

  &.is-bold {
    font-weight: 700;
  }
}

.cl7-diff {
  font-weight: 600;

  &.cl7-diff-up {
    color: #1f9d57;
  }

  &.cl7-diff-down {
    color: #e85251;
  }

  &.cl7-diff-neutral {
    color: #7a7a7a;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/climan/ModalCambiosCalculo.vue